import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import UniversalSinglePageForm from './UniversalSinglePageForm';
import { getSchema } from '../../schemas';
import { Card, CardContent } from '../ui/card';
import { AlertCircle } from 'lucide-react';
import DashboardLayout from '../DashboardLayout';
import LiteLayout from '../lite/LiteLayout';
import LiteAddItemOverlay from '../lite/LiteAddItemOverlay';
import { useAuth } from '../../context/AuthContext';

// Logic imports for safe execution
import { calculateTax, validateHSNCode } from '../../logic/taxEngine';
import { calculateRentalPrice, validateDateRange } from '../../logic/availability';
import { checkStock, calculateVariantInventory } from '../../logic/inventory';

/**
 * Safe Logic Executor
 * Wraps logic execution to prevent crashes from propagating to UI
 */
const safeExecuteLogic = (fn, defaultValue) => {
  try {
    return fn();
  } catch (error) {
    console.error('SafeFormWrapper - Logic execution error:', error);
    return defaultValue;
  }
};

/**
 * SafeFormWrapper - The Protection Layer
 * 
 * Responsibilities:
 * 1. Load schema based on category parameter
 * 2. Execute business logic safely
 * 3. Provide error boundary for render crashes
 * 4. Route to UniversalSinglePageForm
 */
const SafeFormWrapper = () => {
  const { category, listingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Mobile detection for Lite Mode
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Get schema for this category
  const schema = getSchema(category);
  
  // Handle invalid category
  if (!schema) {
    return (
      <DashboardLayout>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="py-8">
            <div className="flex items-center justify-center gap-3">
              <AlertCircle className="h-8 w-8 text-orange-600" />
              <div>
                <h2 className="text-lg font-semibold text-orange-900">
                  Unknown Listing Type
                </h2>
                <p className="text-orange-700">
                  The listing type &quot;{category}&quot; is not recognized.
                </p>
                <p className="text-sm text-orange-600 mt-2">
                  Valid types: product, service, rental, event, classified, subscription, volunteering
                </p>
                <button 
                  onClick={() => navigate('/listings')}
                  className="mt-3 text-sm text-orange-600 hover:underline"
                >
                  ← Back to Listings
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  /**
   * Computed values from business logic
   * These are calculated safely and can be passed to child components
   */
  const computeLogicValues = (formData) => {
    return {
      // Tax calculations
      taxData: safeExecuteLogic(
        () => formData.chargeTax && formData.price 
          ? calculateTax(formData.price, formData.taxRate || 18)
          : null,
        null
      ),
      
      // HSN validation
      hsnValidation: safeExecuteLogic(
        () => formData.hsnCode || formData.hsn_sac_code
          ? validateHSNCode(formData.hsnCode || formData.hsn_sac_code)
          : { valid: true },
        { valid: true }
      ),
      
      // Rental pricing
      rentalPricing: safeExecuteLogic(
        () => schema?.listingType === 'rental' && formData.dailyRate
          ? calculateRentalPrice(
              formData.dailyRate,
              formData.weeklyRate,
              formData.monthlyRate,
              formData.rentalDays || 1
            )
          : null,
        null
      ),
      
      // Inventory status
      stockStatus: safeExecuteLogic(
        () => formData.inventory !== undefined && !formData.hasVariants
          ? checkStock(formData.inventory, 1, formData.continueSellingOutOfStock)
          : null,
        null
      ),
      
      // Variant inventory
      variantInventory: safeExecuteLogic(
        () => formData.hasVariants && formData.variantCombinations
          ? calculateVariantInventory(formData.variantCombinations)
          : null,
        null
      ),
      
      // Date validation for events
      dateValidation: safeExecuteLogic(
        () => formData.startDate
          ? validateDateRange(formData.startDate, formData.endDate)
          : null,
        null
      ),
    };
  };

  // ============ LITE MODE: Use LiteAddItemOverlay for mobile vendors ============
  // This provides a consistent mobile-first editing experience
  const isVendor = user?.role === 'vendor';
  const isLiteMode = isMobile && isVendor && listingId;
  
  if (isLiteMode) {
    return (
      <LiteLayout 
        title="Edit Listing"
        showBackButton={true}
        onBack={() => navigate('/listings')}
      >
        <LiteAddItemOverlay
          isOpen={true}
          onClose={() => navigate('/listings')}
          editMode={true}
          listingId={listingId}
          initialType={category}
          isLiteMode={true}
        />
      </LiteLayout>
    );
  }

  return (
    <ErrorBoundary 
      onReset={() => {
        // Reload the page on error reset
        window.location.reload();
      }}
    >
      <UniversalSinglePageForm 
        computeLogicValues={computeLogicValues}
      />
    </ErrorBoundary>
  );
};

export default SafeFormWrapper;
